/**
 * ConfidenceChip
 * AI confidence badge with Popover flyout showing confidence signals
 */

import { useState } from 'react'
import {
  makeStyles,
  tokens,
  Badge,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Text,
  mergeClasses,
} from '@fluentui/react-components'
import { InfoRegular, CheckmarkCircleRegular, DismissCircleRegular } from '@fluentui/react-icons'
import type { AIConfidence, AIConfidenceSignal } from '../../../domain'

const useStyles = makeStyles({
  badge: {
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
  },
  badgeNonInteractive: {
    cursor: 'default',
  },
  highConfidence: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground2,
  },
  mediumConfidence: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
    color: tokens.colorPaletteYellowForeground2,
  },
  lowConfidence: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground2,
  },
  infoIcon: {
    fontSize: '12px',
  },
  popoverContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    maxWidth: '280px',
  },
  popoverTitle: {
    fontWeight: tokens.fontWeightSemibold,
    marginBottom: tokens.spacingVerticalXS,
  },
  signalList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  signalItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalS,
  },
  positiveIcon: {
    color: tokens.colorPaletteGreenForeground1,
    fontSize: '16px',
    flexShrink: 0,
  },
  negativeIcon: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: '16px',
    flexShrink: 0,
  },
  signalText: {
    color: tokens.colorNeutralForeground1,
  },
})

const confidenceLabels: Record<AIConfidence, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
}

export interface ConfidenceChipProps {
  confidence: AIConfidence
  signals?: AIConfidenceSignal[]
}

export function ConfidenceChip({ confidence, signals = [] }: ConfidenceChipProps) {
  const styles = useStyles()
  const [open, setOpen] = useState(false)

  const confidenceClass =
    confidence === 'high'
      ? styles.highConfidence
      : confidence === 'medium'
        ? styles.mediumConfidence
        : styles.lowConfidence

  if (signals.length === 0) {
    return (
      <Badge
        appearance="tint"
        size="small"
        className={mergeClasses(styles.badge, styles.badgeNonInteractive, confidenceClass)}
      >
        {confidenceLabels[confidence]}
      </Badge>
    )
  }

  const badgeContent = (
    <Badge
      appearance="tint"
      size="small"
      className={mergeClasses(styles.badge, confidenceClass)}
    >
      {confidenceLabels[confidence]}
      <InfoRegular className={styles.infoIcon} />
    </Badge>
  )

  return (
    <Popover open={open} onOpenChange={(_, data) => setOpen(data.open)} positioning="below">
      <PopoverTrigger disableButtonEnhancement>
        <span
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setOpen((prev) => !prev);
            }
          }}
        >
          {badgeContent}
        </span>
      </PopoverTrigger>
      <PopoverSurface>
        <div className={styles.popoverContent}>
          <Text className={styles.popoverTitle}>AI Confidence Signals</Text>
          <div className={styles.signalList}>
            {signals.map((signal, index) => (
              <div key={index} className={styles.signalItem}>
                {signal.isPositive ? (
                  <CheckmarkCircleRegular className={styles.positiveIcon} />
                ) : (
                  <DismissCircleRegular className={styles.negativeIcon} />
                )}
                <Text size={200} className={styles.signalText}>
                  {signal.label}
                </Text>
              </div>
            ))}
          </div>
        </div>
      </PopoverSurface>
    </Popover>
  )
}
